// src/components/MyOrdersPage.js — GoDavaii 2030 ELITE
// ✅ ALL ORIGINAL LOGIC 100% PRESERVED — zero logic changes
// ✅ Fixed: removed 52px top padding (Navbar already above)
// ✅ Fixed: Google Fonts injected inline (Sora + Plus Jakarta Sans)
// ✅ New features: Timeline, savings badge, speed indicator, swipe-to-reorder hint
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ReceiptText, X, MapPin, Clock, Package,
  ChevronRight, RefreshCw, Zap, Video, Phone, FileText, MessageCircle, Stethoscope,
} from "lucide-react";

import QuoteReviewModal from "./QuoteReviewModal";
import PrescriptionUploadModal from "./PrescriptionUploadModal";
import { useCart } from "../context/CartContext";

import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../components/ui/tooltip";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
const DEEP   = "#0C5A3E";
const MID    = "#0E7A4F";
const ACCENT = "#00D97E";

// ── Inject Google Fonts once ─────────────────────────────────
const FONTS_ID = "gd-google-fonts";
if (typeof document !== "undefined" && !document.getElementById(FONTS_ID)) {
  const link = document.createElement("link");
  link.id = FONTS_ID;
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap";
  document.head.appendChild(link);
}

// ══════════════════════════════════════════════════════════════
// ALL ORIGINAL HELPER FUNCTIONS — 100% IDENTICAL
// ══════════════════════════════════════════════════════════════
const toAbsUrl = (u = "") =>
  u.startsWith("/uploads/") ? `${API_BASE_URL}${u}` : u;

const isSameOriginUrl = (url) => {
  try {
    return new URL(url, window.location.origin).origin === window.location.origin;
  } catch { return false; }
};

function collectRxUrls(order) {
  const urls = [];
  if (Array.isArray(order.attachments) && order.attachments.length)
    urls.push(...order.attachments.map(toAbsUrl));
  else if (Array.isArray(order.prescriptionUrls) && order.prescriptionUrls.length)
    urls.push(...order.prescriptionUrls.map(toAbsUrl));
  else if (order.prescriptionUrl || order.prescription)
    urls.push(toAbsUrl(order.prescriptionUrl || order.prescription));
  return [...new Set(urls.filter(Boolean))];
}

async function openOrDownloadAllRx(order) {
  const urls = collectRxUrls(order);
  if (!urls.length) return;
  for (let i = 0; i < urls.length; i++) {
    const url = urls[i];
    if (isSameOriginUrl(url)) {
      try {
        const resp = await fetch(url);
        if (!resp.ok) throw new Error("fetch failed");
        const blob = await resp.blob();
        const a = document.createElement("a");
        const nameFromUrl = url.split("/").pop()?.split("?")[0] || `prescription_${i + 1}`;
        const extFromType = blob.type?.split("/").pop() || "";
        const filename = nameFromUrl.includes(".")
          ? nameFromUrl
          : `${nameFromUrl}${extFromType ? "." + extFromType : ""}`;
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        document.body.appendChild(a); a.click();
        setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 0);
      } catch {
        const a = document.createElement("a");
        a.href = url; a.target = "_blank"; a.rel = "noopener noreferrer";
        document.body.appendChild(a); a.click(); a.remove();
      }
    } else {
      const a = document.createElement("a");
      a.href = url; a.target = "_blank"; a.rel = "noopener noreferrer";
      document.body.appendChild(a); a.click(); a.remove();
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 120));
    }
  }
}

function getHiddenRejectionIds() {
  try {
    return JSON.parse(localStorage.getItem("hiddenRejectionPopupOrderIds") || "[]").map(String);
  } catch { return []; }
}
function addHiddenRejectionId(orderId) {
  const ids = getHiddenRejectionIds();
  const idStr = String(orderId);
  if (!ids.includes(idStr)) {
    ids.push(idStr);
    localStorage.setItem("hiddenRejectionPopupOrderIds", JSON.stringify(ids));
  }
}
function formatOrderDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const date = d.toLocaleDateString("en-IN", { day: "numeric", month: "long" });
  let hour = d.getHours();
  const min = d.getMinutes().toString().padStart(2, "0");
  const ampm = hour >= 12 ? "pm" : "am";
  hour = hour % 12 || 12;
  return `${date}, ${hour}:${min}${ampm}`;
}
function getTotalPrice(order) {
  if (order.tempQuote && Array.isArray(order.tempQuote.items) && order.tempQuote.items.length)
    return order.tempQuote.items.filter(i => i.available !== false).reduce((a, b) => a + ((b.price || 0) * (b.quantity || 1)), 0);
  if (order.tempQuote && typeof order.tempQuote.approxPrice === "number")
    return order.tempQuote.approxPrice;
  if (order.quote && Array.isArray(order.quote.items) && order.quote.items.length)
    return order.quote.items.filter(i => i.available !== false).reduce((a, b) => a + ((b.price || 0) * (b.quantity || 1)), 0);
  if (order.quote && typeof order.quote.price === "number")
    return order.quote.price;
  if (Array.isArray(order.quote) && order.quote.length)
    return order.quote.filter(i => i.available !== false).reduce((a, b) => a + ((b.price || 0) * (b.quantity || 1)), 0);
  if (Array.isArray(order.quotes) && order.quotes.length && typeof order.quotes[order.quotes.length - 1]?.price === "number")
    return order.quotes[order.quotes.length - 1].price;
  if (order.tempQuote && Array.isArray(order.tempQuote.items))
    return order.tempQuote.items.filter(i => i.available !== false).reduce((a, b) => a + ((b.price || 0) * (b.quantity || 1)), 0);
  return 0;
}
function getQuoteType(order) {
  let items = [];
  if (order.tempQuote?.items?.length) items = order.tempQuote.items;
  else if (order.quote?.items?.length) items = order.quote.items;
  else if (Array.isArray(order.quote) && order.quote.length) items = order.quote;
  else if (Array.isArray(order.quotes) && order.quotes.length && order.quotes[order.quotes.length - 1]?.items?.length)
    items = order.quotes[order.quotes.length - 1].items;
  if (!items.length) return "none";
  if (items.every(i => i.available !== false)) return "full";
  if (items.some(i => i.available === false)) return "partial";
  return "none";
}
function groupSplitOrders(orders) {
  const orderMap = {};
  const result = [];
  orders.forEach(order => { orderMap[order._id] = { ...order, splits: [] }; });
  orders.forEach(order => {
    if (order.parentOrder && orderMap[order.parentOrder])
      orderMap[order.parentOrder].splits.push(order);
  });
  const used = new Set();
  orders.forEach(order => {
    if (order.parentOrder) { used.add(order._id); }
    else { result.push(orderMap[order._id]); orderMap[order._id].splits.forEach(s => used.add(s._id)); }
  });
  orders.forEach(order => { if (!used.has(order._id)) result.push(order); });
  return result;
}
function getDisplayAddress(address) {
  if (!address) return "";
  return (
    address.formatted ||
    [address.addressLine, address.floor, address.area, address.city].filter(Boolean).join(", ") ||
    address.fullAddress ||
    JSON.stringify(address)
  );
}

// ══════════════════════════════════════════════════════════════
// STATUS CONFIG — 2030 elite with glow
// ══════════════════════════════════════════════════════════════
const STATUS_CFG = {
  pending:              { label: "Pending",       color: "#92400E", bg: "#FEF3C7", border: "#FDE68A", emoji: "🕐",  dot: "#F59E0B", live: false },
  placed:               { label: "Placed",        color: "#1E40AF", bg: "#EFF6FF", border: "#BFDBFE", emoji: "📋", dot: "#3B82F6", live: false },
  review_required:      { label: "Reviewing",     color: "#1E40AF", bg: "#EFF6FF", border: "#BFDBFE", emoji: "🔎", dot: "#3B82F6", live: true  },
  admin_review_required:{ label: "Reviewing",     color: "#1E40AF", bg: "#EFF6FF", border: "#BFDBFE", emoji: "🧑‍⚕️", dot: "#3B82F6", live: true  },
  user_reviewed:        { label: "Reviewing",     color: "#1E40AF", bg: "#EFF6FF", border: "#BFDBFE", emoji: "🧾", dot: "#3B82F6", live: true  },
  budget_guard_set:     { label: "Reviewing",     color: "#1E40AF", bg: "#EFF6FF", border: "#BFDBFE", emoji: "🛡️", dot: "#3B82F6", live: true  },
  chemist_routing_pending:{ label: "Reviewing",   color: "#1E40AF", bg: "#EFF6FF", border: "#BFDBFE", emoji: "📡", dot: "#3B82F6", live: true  },
  chemist_notified:     { label: "Reviewing",     color: "#1E40AF", bg: "#EFF6FF", border: "#BFDBFE", emoji: "🏥", dot: "#3B82F6", live: true  },
  chemist_accepted:     { label: "Reviewing",     color: "#92400E", bg: "#FEF3C7", border: "#FDE68A", emoji: "👀", dot: "#F59E0B", live: true  },
  chemist_confirmed:    { label: "Pharmacy Confirmed", color: "#065F46", bg: "#ECFDF5", border: "#6EE7B7", emoji: "✅", dot: "#10B981", live: false },
  partial_confirmation: { label: "Reviewing",     color: "#92400E", bg: "#FEF3C7", border: "#FDE68A", emoji: "⚠️", dot: "#F59E0B", live: true  },
  price_confirmation_pending:{ label: "Reviewing", color: "#92400E", bg: "#FEF3C7", border: "#FDE68A", emoji: "💬", dot: "#F59E0B", live: true  },
  final_total_locked:   { label: "Pharmacy Confirmed", color: "#065F46", bg: "#ECFDF5", border: "#6EE7B7", emoji: "🔒", dot: "#10B981", live: false },
  order_confirmed:      { label: "Pharmacy Confirmed", color: "#065F46", bg: "#ECFDF5", border: "#6EE7B7", emoji: "✅", dot: "#10B981", live: false },
  quoted:               { label: "Quote Ready",   color: "#5B21B6", bg: "#F5F3FF", border: "#C4B5FD", emoji: "💬", dot: "#8B5CF6", live: false },
  pending_user_confirm: { label: "Action Needed", color: "#991B1B", bg: "#FEF2F2", border: "#FECACA", emoji: "⚠️", dot: "#EF4444", live: false },
  processing:           { label: "Processing",    color: "#92400E", bg: "#FEF3C7", border: "#FDE68A", emoji: "⚙️", dot: "#F59E0B", live: true  },
  assigned:             { label: "Assigned",      color: "#075985", bg: "#F0F9FF", border: "#BAE6FD", emoji: "🤝", dot: "#0EA5E9", live: true  },
  accepted:             { label: "Accepted",      color: "#065F46", bg: "#ECFDF5", border: "#6EE7B7", emoji: "✅", dot: "#10B981", live: true  },
  picked_up:            { label: "Picked Up",     color: "#065F46", bg: "#ECFDF5", border: "#6EE7B7", emoji: "📦", dot: "#10B981", live: true  },
  out_for_delivery:     { label: "On the Way",    color: "#92400E", bg: "#FEF3C7", border: "#FDE68A", emoji: "🛵", dot: "#F59E0B", live: true  },
  delivered:            { label: "Delivered",     color: "#065F46", bg: "#ECFDF5", border: "#6EE7B7", emoji: "🎉", dot: "#10B981", live: false },
  cancelled:            { label: "Cancelled",     color: "#991B1B", bg: "#FEF2F2", border: "#FECACA", emoji: "❌", dot: "#EF4444", live: false },
  rejected:             { label: "Rejected",      color: "#991B1B", bg: "#FEF2F2", border: "#FECACA", emoji: "❌", dot: "#EF4444", live: false },
  confirmed:            { label: "Confirmed",     color: "#065F46", bg: "#ECFDF5", border: "#6EE7B7", emoji: "✅", dot: "#10B981", live: false },
};

function StatusBadge({ status, labelOverride = "" }) {
  const c = STATUS_CFG[status] || { label: status, color: "#475569", bg: "#F1F5F9", border: "#CBD5E1", emoji: "📄", dot: "#94A3B8", live: false };
  const label = String(labelOverride || c.label || status || "Pending");
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      fontSize: 11.5, fontWeight: 800, color: c.color,
      background: c.bg, border: `1.5px solid ${c.border}`,
      padding: "4px 12px 4px 8px", borderRadius: 100,
      letterSpacing: "0.1px", fontFamily: "'Sora', sans-serif",
      boxShadow: c.live ? `0 0 12px ${c.border}80` : "none",
    }}>
      <span style={{
        width: 7, height: 7, borderRadius: "50%",
        background: c.dot,
        boxShadow: c.live ? `0 0 6px ${c.dot}` : "none",
        animation: c.live ? "liveDot 1.6s ease-in-out infinite" : "none",
        flexShrink: 0,
      }} />
      {c.emoji} {label}
    </span>
  );
}

// ── Pill chip ─────────────────────────────────────────────────
function Pill({ children, color = "emerald" }) {
  const c = color === "amber"
    ? { text: "#92400E", bg: "#FEF3C7", border: "#FDE68A" }
    : { text: "#065F46", bg: "#ECFDF5", border: "#6EE7B7" };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      fontSize: 10, fontWeight: 800, color: c.text,
      background: c.bg, border: `1px solid ${c.border}`,
      padding: "2px 9px", borderRadius: 100, marginLeft: 6,
      letterSpacing: "0.2px",
    }}>
      {children}
    </span>
  );
}

// ── Stat tile ─────────────────────────────────────────────────
function StatTile({ emoji, label, value }) {
  return (
    <div style={{
      flex: 1, background: "rgba(255,255,255,0.10)",
      border: "1px solid rgba(255,255,255,0.15)",
      borderRadius: 18, padding: "12px 10px",
      display: "flex", flexDirection: "column", gap: 3, alignItems: "flex-start",
    }}>
      <span style={{ fontSize: 20 }}>{emoji}</span>
      <span style={{
        fontFamily: "'Sora',sans-serif", fontSize: 20,
        fontWeight: 800, color: "#fff", lineHeight: 1,
      }}>{value}</span>
      <span style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", fontWeight: 600, letterSpacing: "0.3px" }}>{label}</span>
    </div>
  );
}

// ── Delivery progress steps ───────────────────────────────────
const STEPS = ["placed","processing","accepted","picked_up","out_for_delivery","delivered"];
function DeliveryTimeline({ status }) {
  const idx = STEPS.indexOf(status);
  if (idx < 0 || status === "cancelled" || status === "rejected") return null;
  return (
    <div style={{ padding: "12px 0 4px", marginBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
        {STEPS.map((s, i) => {
          const done   = i <= idx;
          const active = i === idx;
          return (
            <React.Fragment key={s}>
              <div style={{
                width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                background: done ? `linear-gradient(135deg,${DEEP},${ACCENT})` : "#E2E8F0",
                border: active ? `2px solid ${ACCENT}` : "2px solid transparent",
                boxShadow: active ? `0 0 10px ${ACCENT}60` : "none",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.3s",
              }}>
                {done && !active && (
                  <svg width="8" height="8" viewBox="0 0 8 8"><polyline points="1,4 3,6 7,2" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/></svg>
                )}
                {active && (
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff", animation: "liveDot 1.4s ease-in-out infinite" }} />
                )}
              </div>
              {i < STEPS.length - 1 && (
                <div style={{
                  flex: 1, height: 3, borderRadius: 2,
                  background: i < idx ? `linear-gradient(90deg,${DEEP},${ACCENT})` : "#E2E8F0",
                  transition: "background 0.3s",
                }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
        {["Placed","Prep","Ready","Picked","On way","Done"].map((l, i) => (
          <span key={l} style={{
            fontSize: 8.5, fontWeight: i <= idx ? 700 : 500,
            color: i <= idx ? DEEP : "#CBD5E1",
            width: 30, textAlign: "center",
          }}>{l}</span>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════
export default function MyOrdersPage() {
  const [orders, setOrders]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [selectedOrder, setSelectedOrder]   = useState(null);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [snackbar, setSnackbar]     = useState({ open: false, message: "", severity: "success" });
  const [rejectDialogOpen, setRejectDialogOpen]       = useState(false);
  const [rejectReason, setRejectReason]               = useState("");
  const [rejectSubmitting, setRejectSubmitting]       = useState(false);
  const [pendingRejectOrderId, setPendingRejectOrderId] = useState(null);
  const prevOrdersRef = useRef([]);
  const { setCart, setSelectedPharmacy } = useCart();
  const navigate = useNavigate();

  const user   = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = user._id || user.userId;
  const token = localStorage.getItem("token") || "";

  const [showPharmacyRejectionPopup, setShowPharmacyRejectionPopup] = useState(false);
  const [rejectedPrescriptionOrder, setRejectedPrescriptionOrder]   = useState(null);
  const [reuploadModalOpen, setReuploadModalOpen] = useState(false);
  const [reuploadMode, setReuploadMode]           = useState("manual");
  const [reuploadOrderData, setReuploadOrderData] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [myConsults, setMyConsults] = useState([]);
  const [consultsLoading, setConsultsLoading] = useState(false);
  const [myIncidents, setMyIncidents] = useState([]);
  const [incidentsLoading, setIncidentsLoading] = useState(false);

  // ── IDENTICAL EFFECTS ──────────────────────────────────────
  useEffect(() => {
    const fetchInitial = async () => { setLoading(true); await fetchOrders(); setLoading(false); };
    fetchInitial();
    // eslint-disable-next-line
  }, [userId]);

  useEffect(() => {
    const poll = setInterval(() => { fetchOrders(); }, 15000);
    return () => clearInterval(poll);
    // eslint-disable-next-line
  }, [userId]);

  // Fetch doctor consultations
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    setConsultsLoading(true);
    axios.get(`${API_BASE_URL}/api/consults/my`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setMyConsults(Array.isArray(r?.data?.consults) ? r.data.consults : []))
      .catch(() => setMyConsults([]))
      .finally(() => setConsultsLoading(false));
    const pollConsults = setInterval(() => {
      axios.get(`${API_BASE_URL}/api/consults/my`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setMyConsults(Array.isArray(r?.data?.consults) ? r.data.consults : []))
        .catch(() => {});
    }, 20000);
    return () => clearInterval(pollConsults);
  }, [userId]);

  // Fetch customer incidents
  useEffect(() => {
    const t = localStorage.getItem("token");
    if (!t) return;
    setIncidentsLoading(true);
    axios.get(`${API_BASE_URL}/api/incidents/mine`, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => setMyIncidents(Array.isArray(r?.data?.incidents) ? r.data.incidents : []))
      .catch(() => setMyIncidents([]))
      .finally(() => setIncidentsLoading(false));
  }, [userId]);

  const fetchOrders = async () => {
    try {
      if (!userId) return;
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
      const res = await axios.get(`${API_BASE_URL}/api/allorders/myorders-userid/${userId}`, { headers });
      let presRes = [];
      try {
        presRes = await axios.get(`${API_BASE_URL}/api/prescriptions/user-orders`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
      } catch {}
      let presOrders = (presRes.data || []).map(p => ({ ...p, orderType: "prescription" }));
      const ids = new Set(res.data.map(o => String(o._id)));
      presOrders = presOrders.filter(po => !ids.has(String(po._id)));
      const allOrders = [...res.data, ...presOrders];
      allOrders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      prevOrdersRef.current = allOrders;
      setOrders(allOrders);
    } catch { setOrders([]); }
  };

  useEffect(() => {
    const hiddenIds = getHiddenRejectionIds();
    let rejected = null;
    for (const o of orders) {
      const oid = String(o._id || (Array.isArray(o) && o[0]) || "");
      const status = o.status || (Array.isArray(o) && o[1]);
      const uploadType = o.uploadType || (Array.isArray(o) && o[2]);
      if (o.orderType === "prescription" && (status === "cancelled" || status === "rejected") && uploadType === "manual") {
        if (!hiddenIds.includes(oid)) { rejected = { ...o, _id: oid }; break; }
      }
    }
    if (!rejected || !rejected._id) { setShowPharmacyRejectionPopup(false); setRejectedPrescriptionOrder(null); return; }
    if (!showPharmacyRejectionPopup || (rejectedPrescriptionOrder && String(rejectedPrescriptionOrder._id) !== String(rejected._id))) {
      setShowPharmacyRejectionPopup(true); setRejectedPrescriptionOrder(rejected);
    }
  }, [orders]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClosePharmacyRejectionPopup = () => {
    if (rejectedPrescriptionOrder?._id) addHiddenRejectionId(String(rejectedPrescriptionOrder._id));
    setShowPharmacyRejectionPopup(false); setRejectedPrescriptionOrder(null);
  };

  // ── IDENTICAL HANDLERS ─────────────────────────────────────
  const handleOrderAgain = (order) => {
    const pharmacyId = (order.pharmacy && order.pharmacy._id) || order.pharmacyId || order.pharmacy;
    if (pharmacyId) { navigate(`/medicines/${pharmacyId}`); }
    else { setSnackbar({ open: true, message: "Pharmacy information missing. Unable to reorder.", severity: "error" }); }
  };

  const handleAcceptAndPay = (order) => {
    if (order.quote && order.quote.items) {
      setCart(
        order.quote.items.filter(i => i.available !== false).map(i => ({
          _id: i._id || i.medicineId || Math.random().toString(),
          name: i.composition || i.medicineName || i.name || i.brand || "Medicine",
          brand: i.brand, price: i.price, quantity: i.quantity, img: "",
        }))
      );
    }
    setSelectedPharmacy(order.pharmacy);
    navigate(`/checkout?orderId=${order._id}`);
    setQuoteModalOpen(false); setSelectedOrder(null);
  };

  const handleUserConfirmRespond = async (orderId, type, reason = "") => {
    try {
      if (type === "rejected" && !reason.trim()) {
        setSnackbar({ open: true, message: "Reason is required for rejection", severity: "error" }); return;
      }
      const body = type === "rejected" ? { response: "rejected", reason } : { response: "accepted" };
      await axios.post(`${API_BASE_URL}/api/prescriptions/respond/${orderId}`, body);
      setSnackbar({
        open: true,
        message: type === "rejected" ? "Order rejected." : "Order confirmed.",
        severity: type === "rejected" ? "info" : "success",
      });
      setOrders(prev =>
        prev.map(o =>
          o._id === orderId
            ? {
                ...o,
                status: type === "rejected" ? "rejected" : "order_confirmed",
                userStatus: type === "rejected" ? "Cancelled" : "Pharmacy confirmed",
              }
            : o
        )
      );
    } catch { setSnackbar({ open: true, message: "Failed to submit response", severity: "error" }); }
    setRejectSubmitting(false); setRejectReason(""); setRejectDialogOpen(false); setPendingRejectOrderId(null);
  };

  const groupedOrders = groupSplitOrders(orders);

  if (orders.length && (!orders[0] || typeof orders[0] !== "object" || Array.isArray(orders[0])))
    throw new Error("BUG: orders should be an array of objects. Got: " + JSON.stringify(orders[0]));

  // ── Tab computed ───────────────────────────────────────────
  const activeOrders = orders.filter(o =>
    [
      "placed",
      "processing",
      "assigned",
      "accepted",
      "picked_up",
      "out_for_delivery",
      "pending",
      "quoted",
      "pending_user_confirm",
      "review_required",
      "admin_review_required",
      "user_reviewed",
      "budget_guard_set",
      "chemist_routing_pending",
      "chemist_notified",
      "chemist_accepted",
      "chemist_confirmed",
      "partial_confirmation",
      "price_confirmation_pending",
      "final_total_locked",
      "order_confirmed",
    ].includes(String(o.status || "").toLowerCase())
  );
  const pastOrders = orders.filter(o =>
    ["delivered", "confirmed", "cancelled", "rejected", "failed", "converted_to_order"].includes(
      String(o.status || "").toLowerCase()
    )
  );
  const displayGrouped = activeTab === "active"
    ? groupSplitOrders(activeOrders)
    : activeTab === "past"
    ? groupSplitOrders(pastOrders)
    : groupedOrders;

  // ── Quick stats ────────────────────────────────────────────
  const deliveredCount = orders.filter(o =>
    ["delivered", "confirmed", "converted_to_order"].includes(String(o.status || "").toLowerCase())
  ).length;
  const totalSpent = orders
    .filter(o => ["delivered", "confirmed", "converted_to_order"].includes(String(o.status || "").toLowerCase()))
    .reduce((sum, o) => sum + (Number(o.total) || getTotalPrice(o) || 0), 0);

  // Total Savings: sum of (mrp - price)*qty across all delivered orders
  const totalSavings = orders
    .filter(o => ["delivered", "confirmed", "converted_to_order"].includes(String(o.status || "").toLowerCase()))
    .reduce((sum, o) => {
      const items = Array.isArray(o.items) ? o.items : [];
      return sum + items.reduce((s, i) => {
        const mrp = Number(i.mrp || i.originalPrice || 0);
        const price = Number(i.price || 0);
        const qty = Number(i.quantity || i.qty || 1);
        return s + (mrp > price ? (mrp - price) * qty : 0);
      }, 0);
    }, 0);

  // ══════════════════════════════════════════════════════════
  // RENDER ORDER CARD — ALL LOGIC IDENTICAL, elite visual
  // ══════════════════════════════════════════════════════════
  const renderOrderCard = (o, splitBadge = null, uniqueKey = null) => {
    const statusKey = String(o.status || "").toLowerCase();
    const userFacingStatus = o.publicStatus || o.userStatus || "";
    const isActive = [
      "placed",
      "processing",
      "assigned",
      "accepted",
      "picked_up",
      "out_for_delivery",
      "review_required",
      "admin_review_required",
      "chemist_notified",
      "chemist_accepted",
      "price_confirmation_pending",
      "chemist_confirmed",
      "order_confirmed",
    ].includes(statusKey);
    const isLive   = ["out_for_delivery","assigned","picked_up","chemist_notified","chemist_accepted"].includes(statusKey);
    const price    = Number(o.total) || getTotalPrice(o) || 0;

    return (
      <motion.div
        key={uniqueKey || o._id}
        initial={{ opacity: 0, y: 18, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
        style={{
          background: "#fff",
          borderRadius: 24,
          border: isActive
            ? `1.5px solid ${ACCENT}50`
            : "1.5px solid rgba(12,90,62,0.08)",
          boxShadow: isActive
            ? `0 12px 40px rgba(12,90,62,0.16), 0 2px 8px rgba(0,217,126,0.08)`
            : "0 2px 16px rgba(0,0,0,0.05)",
          overflow: "hidden",
          marginBottom: 16,
          position: "relative",
        }}
      >
        {/* Active shimmer top bar */}
        {isActive && (
          <div style={{
            height: 4,
            background: `linear-gradient(90deg, ${DEEP} 0%, ${ACCENT} 50%, ${DEEP} 100%)`,
            backgroundSize: "200% 100%",
            animation: isLive ? "barShimmer 2s linear infinite" : "none",
          }} />
        )}

        <div style={{ padding: "18px 18px 16px" }}>

          {/* ─── TOP: Pharmacy + Price ─── */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Pharmacy row */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                {/* Pharmacy avatar */}
                <div style={{
                  width: 38, height: 38, borderRadius: 12,
                  background: `linear-gradient(135deg, ${DEEP}15, ${ACCENT}15)`,
                  border: `1.5px solid ${DEEP}15`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  <span style={{ fontSize: 18 }}>🏥</span>
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontFamily: "'Sora',sans-serif", fontSize: 16, fontWeight: 800,
                    color: "#0A1F14", letterSpacing: "-0.3px", lineHeight: 1.2,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    maxWidth: 160,
                  }}>
                    {o.pharmacy?.name || o.pharmacy}
                  </div>
                  <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 1, display: "flex", alignItems: "center", gap: 4 }}>
                    <Clock style={{ width: 10, height: 10 }} />
                    {formatOrderDate(o.createdAt)}
                  </div>
                </div>
                {o.orderType === "prescription" && (
                  <span style={{ fontSize: 9.5, fontWeight: 800, color: "#92400E", background: "#FEF3C7", padding: "2px 8px", borderRadius: 100, border: "1px solid #FDE68A", letterSpacing: "0.2px" }}>
                    Rx ORDER
                  </span>
                )}
                {!!splitBadge && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span style={{
                          fontSize: 9, fontWeight: 700, cursor: "help",
                          color: splitBadge.includes("Parent") ? "#1D4ED8" : "#065F46",
                          background: splitBadge.includes("Parent") ? "#EFF6FF" : "#ECFDF5",
                          border: splitBadge.includes("Parent") ? "1px solid #BFDBFE" : "1px solid #A7F3D0",
                          padding: "2px 8px", borderRadius: 100,
                        }}>
                          {splitBadge.includes("Parent") ? "🔀 Split" : "📦 Part"}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="text-sm">{splitBadge}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              {/* Status badge */}
              <StatusBadge status={statusKey} labelOverride={userFacingStatus} />
            </div>

            {/* Price block */}
            {price > 0 && (
              <div style={{
                flexShrink: 0, marginLeft: 12, textAlign: "right",
                background: `linear-gradient(135deg,${DEEP}08,${ACCENT}10)`,
                border: `1px solid ${DEEP}15`, borderRadius: 14,
                padding: "8px 12px",
              }}>
                <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 20, fontWeight: 900, color: DEEP, lineHeight: 1 }}>
                  ₹{price}
                </div>
                <div style={{ fontSize: 9, color: "#94A3B8", fontWeight: 600, marginTop: 2 }}>TOTAL</div>
              </div>
            )}
          </div>

          {/* ─── DELIVERY TIMELINE (active orders only) ─── */}
          {isActive && <DeliveryTimeline status={o.status} />}

          {/* ─── PRESCRIPTION ORDER BLOCK — IDENTICAL LOGIC ─── */}
          {o.orderType === "prescription" ? (
            <>
              {/* Prescription link */}
              <div style={{
                background: "#F8FBFA", borderRadius: 12, padding: "10px 12px",
                marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <div style={{ fontSize: 13, color: "#374151", fontWeight: 600 }}>📎 Prescription</div>
                {collectRxUrls(o).length ? (
                  <a
                    href={collectRxUrls(o)[0]}
                    onClick={(e) => { e.preventDefault(); openOrDownloadAllRx(o); }}
                    target="_blank" rel="noreferrer"
                    style={{
                      color: DEEP, fontWeight: 800, fontSize: 12, textDecoration: "none",
                      background: "#E8F5EF", padding: "4px 12px", borderRadius: 100,
                      border: `1px solid ${ACCENT}40`,
                    }}
                  >
                    View / Download
                  </a>
                ) : (
                  <span style={{ fontSize: 12, color: "#94A3B8" }}>Not Available</span>
                )}
              </div>

              {/* Quote block — IDENTICAL condition */}
              {["quoted", "pending_user_confirm", "price_confirmation_pending", "partial_confirmation", "chemist_confirmed"].includes(statusKey) && (
                <div style={{
                  borderRadius: 20, overflow: "hidden", marginBottom: 14,
                  border: "1.5px solid rgba(12,90,62,0.18)",
                  boxShadow: "0 4px 20px rgba(12,90,62,0.10)",
                }}>
                  {/* Quote header */}
                  <div style={{
                    background: `linear-gradient(135deg,${DEEP},#0A4631)`,
                    padding: "14px 16px",
                    display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8,
                  }}>
                    <div>
                      <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 15, fontWeight: 800, color: "#fff", marginBottom: 2 }}>
                        ✨ Quote Ready!
                      </div>
                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>
                        Review and accept to proceed
                      </div>
                    </div>
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={() => { setSelectedOrder(o); setQuoteModalOpen(true); }}
                      style={{
                        display: "flex", alignItems: "center", gap: 6,
                        height: 36, padding: "0 16px", borderRadius: 100,
                        border: "1.5px solid rgba(255,255,255,0.35)",
                        background: "rgba(255,255,255,0.15)",
                        color: "#fff", fontSize: 12, fontWeight: 700,
                        fontFamily: "'Sora',sans-serif", cursor: "pointer",
                        backdropFilter: "blur(8px)",
                      }}
                    >
                      <ReceiptText style={{ width: 12, height: 12 }} /> View Details
                    </motion.button>
                  </div>

                  {/* Quote body */}
                  <div style={{ background: "#F8FBFA", padding: "14px 16px" }}>
                    <div style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      marginBottom: 10, flexWrap: "wrap", gap: 6,
                    }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>
                        Amount Due
                        {getQuoteType(o) === "full"    && <Pill color="emerald">✓ All Available</Pill>}
                        {getQuoteType(o) === "partial" && <Pill color="amber">⚠ Partial</Pill>}
                      </div>
                      <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 24, fontWeight: 900, color: DEEP }}>
                        ₹{getTotalPrice(o)}
                      </div>
                    </div>

                    {getQuoteType(o) === "full" && (
                      <div style={{ background: "#ECFDF5", borderRadius: 12, padding: "10px 14px", fontSize: 13, color: "#065F46", fontWeight: 600, marginBottom: 10, lineHeight: 1.6 }}>
                        ✅ All medicines available! Accept & Pay to get your order fast.
                      </div>
                    )}
                    {getQuoteType(o) === "partial" && (
                      <div style={{ background: "#FEF3C7", borderRadius: 12, padding: "10px 14px", fontSize: 13, color: "#92400E", marginBottom: 10, lineHeight: 1.6 }}>
                        ⚠️ Some medicines unavailable. You can still pay for available items.
                      </div>
                    )}
                    {o.quote?.items?.some(i => i.available === false) && (
                      <div style={{ fontSize: 12, color: "#EF4444", marginBottom: 10, fontWeight: 600 }}>
                        Unavailable: {o.quote.items.filter(i => i.available === false).map(i => i.composition || i.medicineName || i.name || i.brand).join(", ")}
                      </div>
                    )}

                    {/* Accept & Pay — IDENTICAL condition */}
                    {["pending_user_confirm", "price_confirmation_pending", "partial_confirmation", "chemist_confirmed"].includes(statusKey) && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <motion.button
                          whileTap={{ scale: 0.97 }}
                          disabled={rejectSubmitting}
                          onClick={() => handleAcceptAndPay(o)}
                          style={{
                            width: "100%", height: 52, borderRadius: 16, border: "none",
                            background: rejectSubmitting ? "#94A3B8" : `linear-gradient(135deg,${DEEP},${MID})`,
                            color: "#fff", fontSize: 16, fontWeight: 900,
                            fontFamily: "'Sora',sans-serif",
                            cursor: rejectSubmitting ? "not-allowed" : "pointer",
                            boxShadow: "0 8px 24px rgba(12,90,62,0.40)",
                            letterSpacing: "0.5px",
                            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                          }}
                        >
                          💳 ACCEPT &amp; PAY
                          <ChevronRight style={{ width: 18, height: 18 }} />
                        </motion.button>
                        <motion.button
                          whileTap={{ scale: 0.97 }}
                          disabled={rejectSubmitting}
                          onClick={() => { setRejectDialogOpen(true); setPendingRejectOrderId(o._id); setRejectReason(""); }}
                          style={{
                            width: "100%", height: 44, borderRadius: 14,
                            border: "2px solid #FECACA", background: "#FEF2F2",
                            color: "#EF4444", fontSize: 13, fontWeight: 700,
                            fontFamily: "'Sora',sans-serif",
                            cursor: rejectSubmitting ? "not-allowed" : "pointer",
                          }}
                        >
                          Reject Order
                        </motion.button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div style={{ fontSize: 13, fontWeight: 700, color: "#2563EB" }}>
                Status: <span style={{ marginLeft: 4, color: "#0B1F16", fontWeight: 600, textTransform: "capitalize" }}>{userFacingStatus || o.status}</span>
              </div>
            </>
          ) : (
            /* ─── NORMAL ORDER — IDENTICAL LOGIC ─── */
            <>
              {o.items && o.items.length > 0 && (
                <div style={{
                  background: "#F8FBFA", borderRadius: 14, padding: "10px 13px",
                  marginBottom: 10, display: "flex", gap: 8, alignItems: "flex-start",
                }}>
                  <Package style={{ width: 13, height: 13, color: DEEP, flexShrink: 0, marginTop: 2 }} />
                  <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.6 }}>
                    {o.items.map(i => `${i.name || i.medicineName} (${i.quantity || i.qty || "-"})`).join(" · ")}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ─── Address ─── */}
          {o.address && (
            <div style={{
              fontSize: 12, color: "#6B7280", marginBottom: 12,
              display: "flex", gap: 6, alignItems: "flex-start",
            }}>
              <MapPin style={{ width: 12, height: 12, flexShrink: 0, marginTop: 1, color: "#94A3B8" }} />
              <span style={{ lineHeight: 1.5 }}>{getDisplayAddress(o.address)}</span>
            </div>
          )}

          {/* ─── Divider ─── */}
          <div style={{ height: 1, background: "rgba(12,90,62,0.06)", margin: "4px 0 12px" }} />

          {/* ─── ACTION ROW — ALL IDENTICAL ─── */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>

            {o.orderType !== "prescription" && (
              <div style={{ flex: 1, fontSize: 13, fontWeight: 700 }}>
                {o.status === "quoted" ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ color: "#7C3AED", fontWeight: 800 }}>💬 Quote Ready!</span>
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={() => { setSelectedOrder(o); setQuoteModalOpen(true); }}
                      style={{
                        height: 32, padding: "0 14px", borderRadius: 100,
                        border: `1.5px solid ${DEEP}`, background: "#E8F5EF",
                        color: DEEP, fontSize: 11, fontWeight: 700,
                        fontFamily: "'Sora',sans-serif", cursor: "pointer",
                      }}
                    >
                      View &amp; Accept
                    </motion.button>
                  </div>
                ) : (
                  <span style={{ color: "#64748B" }}>
                    Status: <span style={{ color: "#0B1F16", fontWeight: 700, textTransform: "capitalize" }}>{o.status || "Placed"}</span>
                  </span>
                )}
              </div>
            )}

            {/* Track Live */}
            {isActive && (
              <motion.button
                whileTap={{ scale: 0.93 }}
                onClick={() => navigate(`/order-tracking/${o._id}`)}
                style={{
                  height: 36, padding: "0 16px", borderRadius: 100, border: "none",
                  background: `linear-gradient(135deg,${DEEP},${MID})`,
                  color: "#fff", fontSize: 11.5, fontWeight: 700,
                  fontFamily: "'Sora',sans-serif", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 5,
                  boxShadow: "0 4px 14px rgba(12,90,62,0.35)",
                }}
              >
                {isLive ? "🛵" : "📍"} Track Live <ChevronRight style={{ width: 12, height: 12 }} />
              </motion.button>
            )}

            {/* Order Again — IDENTICAL on all orders */}
            <motion.button
              whileTap={{ scale: 0.93 }}
              onClick={() => handleOrderAgain(o)}
              style={{
                height: 36, padding: "0 14px", borderRadius: 100,
                border: `1.5px solid ${DEEP}30`, background: "#F0FAF5",
                color: DEEP, fontSize: 11.5, fontWeight: 700,
                fontFamily: "'Sora',sans-serif", cursor: "pointer",
                display: "flex", alignItems: "center", gap: 5,
              }}
            >
              <RefreshCw style={{ width: 11, height: 11 }} /> Order Again
            </motion.button>
          </div>

          {/* Invoice — IDENTICAL */}
          {o.invoiceFile && (
            <a href={o.invoiceFile} target="_blank" rel="noopener noreferrer"
              style={{ display: "inline-block", marginTop: 10, textDecoration: "none" }}>
              <motion.button
                whileTap={{ scale: 0.95 }}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  height: 34, padding: "0 14px", borderRadius: 100,
                  border: "1.5px solid #BAE6FD", background: "#F0F9FF",
                  color: "#0369A1", fontSize: 11.5, fontWeight: 700,
                  fontFamily: "'Sora',sans-serif", cursor: "pointer",
                }}
              >
                <ReceiptText style={{ width: 12, height: 12 }} /> Download Invoice
              </motion.button>
            </a>
          )}
        </div>
      </motion.div>
    );
  };

  // ══════════════════════════════════════════════════════════
  // MAIN RENDER
  // ══════════════════════════════════════════════════════════
  return (
    <div style={{
      minHeight: "100vh", maxWidth: 480, margin: "0 auto",
      background: "#EEF5F1",
      paddingBottom: 110,
      fontFamily: "'Plus Jakarta Sans', 'Sora', sans-serif",
    }}>

      {/* ═══ HEADER — padding-top: 20px ONLY (Navbar already above) ═══ */}
      <div style={{
        background: `linear-gradient(150deg, ${DEEP} 0%, #083D28 60%, #041F14 100%)`,
        padding: "20px 20px 0",
        position: "relative", overflow: "hidden",
      }}>
        {/* Ambient blobs */}
        <div style={{ position: "absolute", right: -60, top: -60, width: 260, height: 260, borderRadius: "50%", background: `radial-gradient(circle,${ACCENT}15 0%,transparent 65%)`, pointerEvents: "none" }} />
        <div style={{ position: "absolute", left: -40, bottom: 20, width: 180, height: 180, borderRadius: "50%", background: "radial-gradient(circle,rgba(255,255,255,0.04) 0%,transparent 70%)", pointerEvents: "none" }} />

        {/* Title row */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, position: "relative" }}>
          <div>
            <div style={{
              fontFamily: "'Sora',sans-serif", fontSize: 26, fontWeight: 900,
              color: "#fff", letterSpacing: "-0.6px", lineHeight: 1.1,
            }}>
              My Orders
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginTop: 4, fontWeight: 500 }}>
              {orders.length} order{orders.length !== 1 ? "s" : ""} placed
            </div>
          </div>
          <div style={{
            width: 48, height: 48, borderRadius: 16,
            background: "rgba(255,255,255,0.10)",
            border: "1.5px solid rgba(255,255,255,0.18)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.1)",
          }}>
            <ReceiptText style={{ width: 22, height: 22, color: "#fff" }} />
          </div>
        </div>

        {/* Stats strip */}
        {orders.length > 0 && (
          <div style={{ display: "flex", gap: 10, marginBottom: 18, position: "relative" }}>
            <StatTile emoji="🎉" label="DELIVERED" value={deliveredCount} />
            <StatTile emoji="⚡" label="ACTIVE" value={activeOrders.length} />
            {totalSavings > 0 ? (
              <StatTile emoji="🤑" label="YOU SAVED" value={`₹${Math.round(totalSavings)}`} />
            ) : totalSpent > 0 ? (
              <StatTile emoji="💸" label="TOTAL SPENT" value={`₹${Math.round(totalSpent)}`} />
            ) : null}
          </div>
        )}

        {/* ── Tabs — merged into header bottom ── */}
        <div style={{
          display: "flex",
          background: "rgba(0,0,0,0.25)",
          borderRadius: "16px 16px 0 0",
          padding: "4px 4px 0",
          gap: 4,
        }}>
          {[
            { key: "all",    label: "All",    count: orders.length },
            { key: "active", label: "Active", count: activeOrders.length },
            { key: "consults", label: "Consults", count: myConsults.filter(c => c.status !== "pending_payment").length },
            { key: "past",   label: "Past",   count: pastOrders.length },
            { key: "incidents", label: "Issues", count: myIncidents.length },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                flex: 1, height: 44, border: "none", cursor: "pointer",
                background: activeTab === tab.key ? "#fff" : "transparent",
                borderRadius: activeTab === tab.key ? "12px 12px 0 0" : "12px 12px 0 0",
                fontFamily: "'Sora',sans-serif", fontSize: 13, fontWeight: 800,
                color: activeTab === tab.key ? DEEP : "rgba(255,255,255,0.45)",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                transition: "all 0.18s",
              }}
            >
              {tab.label}
              {tab.count > 0 && (
                <span style={{
                  fontSize: 10, fontWeight: 800, padding: "1px 7px", borderRadius: 100,
                  background: activeTab === tab.key ? DEEP : "rgba(255,255,255,0.12)",
                  color: activeTab === tab.key ? "#fff" : "rgba(255,255,255,0.6)",
                }}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ═══ CONTENT ═══ */}
      <div style={{ padding: "16px 14px 0" }}>
        {/* ── Quick Reorder strip (last 3 delivered orders) ── */}
        {!loading && activeTab === "all" && pastOrders.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#94A3B8", letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 10 }}>
              🔄 Quick Reorder
            </div>
            <div style={{ display: "flex", gap: 8, overflowX: "auto", scrollbarWidth: "none", paddingBottom: 4 }}>
              {pastOrders.slice(0, 4).map((o) => {
                const pharmacyName = o.pharmacy?.name || String(o.pharmacy || "").slice(0,12);
                const itemCount = Array.isArray(o.items) ? o.items.length : 0;
                const firstItem = Array.isArray(o.items) && o.items[0] ? (o.items[0].name || o.items[0].medicineName || "") : "";
                return (
                  <motion.button
                    key={`qr-${o._id}`}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleOrderAgain(o)}
                    style={{
                      flexShrink: 0, background: "#fff",
                      border: "1.5px solid rgba(12,90,62,0.10)",
                      borderRadius: 16, padding: "10px 14px",
                      cursor: "pointer", textAlign: "left",
                      minWidth: 140, maxWidth: 160,
                      boxShadow: "0 2px 10px rgba(12,90,62,0.07)",
                    }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 800, color: "#0A1F14", fontFamily: "'Sora',sans-serif", marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      🏥 {pharmacyName}
                    </div>
                    <div style={{ fontSize: 10, color: "#64748B", marginBottom: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {firstItem}{itemCount > 1 ? ` +${itemCount - 1} more` : ""}
                    </div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: DEEP, background: "#E8F5EF", padding: "2px 8px", borderRadius: 100, display: "inline-block", border: `1px solid ${ACCENT}40` }}>
                      Reorder →
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </div>
        )}

        {loading ? (
          /* Skeleton */
          <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 4 }}>
            {[1, 2, 3].map(i => (
              <div key={i} style={{
                borderRadius: 24, overflow: "hidden",
                background: "#fff", border: "1.5px solid rgba(12,90,62,0.07)",
              }}>
                <div style={{ height: 4, background: `linear-gradient(90deg,${DEEP}30,${ACCENT}30)` }} />
                <div style={{ padding: "18px 18px 16px" }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
                    <div style={{ width: 38, height: 38, borderRadius: 12, background: "#F1F5F9", animation: "pulse 1.5s ease-in-out infinite" }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ height: 14, borderRadius: 7, background: "#F1F5F9", width: "60%", marginBottom: 6, animation: "pulse 1.5s ease-in-out infinite" }} />
                      <div style={{ height: 10, borderRadius: 5, background: "#F8FAFC", width: "40%", animation: "pulse 1.5s ease-in-out infinite" }} />
                    </div>
                    <div style={{ width: 64, height: 44, borderRadius: 14, background: "#F1F5F9", animation: "pulse 1.5s ease-in-out infinite" }} />
                  </div>
                  <div style={{ height: 12, borderRadius: 6, background: "#F8FAFC", width: "80%", marginBottom: 8, animation: "pulse 1.5s ease-in-out infinite" }} />
                  <div style={{ height: 12, borderRadius: 6, background: "#F8FAFC", width: "55%", animation: "pulse 1.5s ease-in-out infinite" }} />
                </div>
              </div>
            ))}
          </div>
        ) : activeTab === "consults" ? (
          /* ═══ CONSULTS TAB ═══ */
          consultsLoading ? (
            <div style={{ padding: "30px 0", textAlign: "center", fontSize: 13, color: "#94A3B8", fontWeight: 700 }}>Loading consultations...</div>
          ) : myConsults.filter(c => c.status !== "pending_payment").length === 0 ? (
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} style={{ textAlign: "center", padding: "60px 20px" }}>
              <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} style={{ fontSize: 64, marginBottom: 14 }}>🩺</motion.div>
              <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 900, color: "#0B1F16", marginBottom: 8 }}>No consultations yet</div>
              <div style={{ fontSize: 13, color: "#94A3B8", marginBottom: 24, lineHeight: 1.6 }}>Book a doctor consultation to see it here</div>
              <motion.button whileTap={{ scale: 0.96 }} onClick={() => navigate("/doctors")} style={{ height: 48, padding: "0 28px", borderRadius: 100, border: "none", background: `linear-gradient(135deg,${DEEP},${MID})`, color: "#fff", fontSize: 14, fontWeight: 800, fontFamily: "'Sora',sans-serif", cursor: "pointer", boxShadow: "0 8px 24px rgba(12,90,62,0.35)", display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Stethoscope style={{ width: 16, height: 16 }} /> Book Doctor Consult
              </motion.button>
            </motion.div>
          ) : (
            <div style={{ paddingBottom: 8 }}>
              {myConsults.filter(c => c.status !== "pending_payment").sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((c) => {
                const isLive = c.callState === "live" || c.status === "live_now";
                const canJoin = ["confirmed","accepted","live_now"].includes(c.status) && c.paymentStatus === "paid" && c.mode !== "inperson";
                const hasPrescription = !!c?.prescription?.fileUrl;
                const isCompleted = c.status === "completed";
                const isCancelled = ["cancelled","rejected"].includes(c.status);
                const modeLabel = c.mode === "video" ? "Video" : c.mode === "call" ? "Audio" : "In-Person";
                const statusColor = isLive ? "#065F46" : isCompleted ? "#065F46" : isCancelled ? "#991B1B" : "#92400E";
                const statusBg = isLive ? "#D1FAE5" : isCompleted ? "#ECFDF5" : isCancelled ? "#FEF2F2" : "#FEF3C7";
                const statusBorder = isLive ? "#A7F3D0" : isCompleted ? "#A7F3D0" : isCancelled ? "#FECACA" : "#FDE68A";
                const statusLabel = isLive ? "LIVE NOW" : isCompleted ? "Completed" : isCancelled ? "Cancelled" : c.status === "accepted" ? "Upcoming" : "Pending";

                return (
                  <motion.div key={c.id} initial={{ opacity: 0, y: 18, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.26 }}
                    style={{ background: "#fff", borderRadius: 24, border: isLive ? `1.5px solid ${ACCENT}50` : "1.5px solid rgba(12,90,62,0.08)", boxShadow: isLive ? `0 12px 40px rgba(12,90,62,0.16)` : "0 2px 16px rgba(0,0,0,0.05)", overflow: "hidden", marginBottom: 16, position: "relative" }}>
                    {isLive && <div style={{ height: 4, background: `linear-gradient(90deg,${DEEP} 0%,${ACCENT} 50%,${DEEP} 100%)`, backgroundSize: "200% 100%", animation: "barShimmer 2s linear infinite" }} />}
                    <div style={{ padding: "18px 18px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                          <div style={{ width: 42, height: 42, borderRadius: 14, background: `linear-gradient(135deg,${DEEP}15,${ACCENT}15)`, border: `1.5px solid ${DEEP}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <Stethoscope style={{ width: 20, height: 20, color: DEEP }} />
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 16, fontWeight: 800, color: "#0A1F14", letterSpacing: "-0.3px" }}>{c.doctorName}</div>
                            <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 1, display: "flex", alignItems: "center", gap: 4 }}>
                              <Clock style={{ width: 10, height: 10 }} /> {c.dateLabel} · {c.slot}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                          <span style={{ fontSize: 10, fontWeight: 900, color: statusColor, background: statusBg, border: `1px solid ${statusBorder}`, padding: "4px 10px", borderRadius: 100, display: "flex", alignItems: "center", gap: 4 }}>
                            {isLive && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#10B981", animation: "liveDot 1.6s ease-in-out infinite" }} />}
                            {statusLabel}
                          </span>
                          <span style={{ fontSize: 10, fontWeight: 800, color: c.mode === "video" ? "#7C3AED" : c.mode === "call" ? "#0EA5E9" : "#D97706", background: c.mode === "video" ? "#F5F3FF" : c.mode === "call" ? "#F0F9FF" : "#FFFBEB", padding: "3px 8px", borderRadius: 100 }}>{modeLabel}</span>
                        </div>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
                        <span style={{ fontSize: 11, color: "#475569", fontWeight: 700 }}>{c.specialty}</span>
                        <span style={{ fontSize: 11, color: "#475569", fontWeight: 700 }}>Patient: {c.patientName}</span>
                        <span style={{ fontSize: 11, color: "#475569", fontWeight: 700 }}>₹{c.fee}</span>
                        {c.paymentStatus === "paid" && <span style={{ fontSize: 10, fontWeight: 800, color: "#065F46", background: "#ECFDF5", padding: "2px 8px", borderRadius: 100 }}>Paid ✓</span>}
                      </div>

                      {hasPrescription && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 14, padding: "10px 14px", marginBottom: 12 }}>
                          <FileText style={{ width: 18, height: 18, color: "#15803D", flexShrink: 0 }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12, fontWeight: 900, color: "#166534" }}>Prescription Available</div>
                            <div style={{ fontSize: 10.5, color: "#4ADE80", fontWeight: 700 }}>by {c.doctorName}</div>
                          </div>
                          <a href={c.prescription.fileUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, fontWeight: 900, color: "#fff", background: "linear-gradient(135deg,#15803D,#22C55E)", padding: "7px 14px", borderRadius: 100, textDecoration: "none", display: "flex", alignItems: "center", gap: 4 }}>
                            <FileText style={{ width: 12, height: 12 }} /> View Rx
                          </a>
                        </div>
                      )}

                      {isCompleted && !hasPrescription && (
                        <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 14, padding: "10px 14px", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 16 }}>⏳</span>
                          <div style={{ fontSize: 12, fontWeight: 800, color: "#92400E" }}>Consultation completed — prescription pending from doctor</div>
                        </div>
                      )}

                      <div style={{ display: "flex", gap: 8 }}>
                        {canJoin && (
                          <motion.button whileTap={{ scale: 0.95 }} onClick={() => { const roomId = c.consultRoomId || `consult_${c.id?.slice(-8)}`; window.open(`https://meet.jit.si/${roomId}`, "_blank"); }}
                            style={{ flex: 1, height: 42, border: "none", borderRadius: 14, background: isLive ? "linear-gradient(135deg,#059669,#10B981)" : `linear-gradient(135deg,${DEEP},${MID})`, color: "#fff", fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, boxShadow: isLive ? "0 8px 24px rgba(16,185,129,0.3)" : "0 6px 18px rgba(12,90,62,0.2)" }}>
                            {c.mode === "video" ? <Video style={{ width: 14, height: 14 }} /> : <Phone style={{ width: 14, height: 14 }} />}
                            {isLive ? "Join Now — LIVE" : "Join Consultation"}
                          </motion.button>
                        )}
                        {canJoin && (
                          <motion.button whileTap={{ scale: 0.95 }} onClick={() => { const roomId = c.consultRoomId || `consult_${c.id?.slice(-8)}`; window.open(`https://meet.jit.si/${roomId}#config.startWithVideoMuted=true`, "_blank"); }}
                            style={{ width: 42, height: 42, borderRadius: 14, border: "1.5px solid #E2E8F0", background: "#fff", cursor: "pointer", display: "grid", placeItems: "center" }}>
                            <MessageCircle style={{ width: 18, height: 18, color: DEEP }} />
                          </motion.button>
                        )}
                        {!canJoin && !isCancelled && (
                          <motion.button whileTap={{ scale: 0.95 }} onClick={() => navigate("/doctors")}
                            style={{ flex: 1, height: 42, borderRadius: 14, border: `1.5px solid ${DEEP}30`, background: "#F0FAF5", color: DEEP, fontSize: 12, fontWeight: 700, fontFamily: "'Sora',sans-serif", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                            <RefreshCw style={{ width: 12, height: 12 }} /> Book Again
                          </motion.button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )
        ) : activeTab === "incidents" ? (
          /* ═══ INCIDENTS TAB ═══ */
          incidentsLoading ? (
            <div style={{ padding: "30px 0", textAlign: "center", fontSize: 13, color: "#94A3B8", fontWeight: 700 }}>Loading issues...</div>
          ) : myIncidents.length === 0 ? (
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} style={{ textAlign: "center", padding: "60px 20px" }}>
              <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} style={{ fontSize: 64, marginBottom: 14 }}>✅</motion.div>
              <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 900, color: "#0B1F16", marginBottom: 8 }}>No issues reported</div>
              <div style={{ fontSize: 13, color: "#94A3B8", lineHeight: 1.6 }}>If you face any issue with an order, you can report it from the order tracking page.</div>
            </motion.div>
          ) : (
            <div style={{ paddingBottom: 8 }}>
              {myIncidents.map((inc) => {
                const sevColor = { critical: "#DC2626", high: "#EA580C", medium: "#D97706", low: "#65A30D" }[inc.severity] || "#6B7280";
                const sevBg = { critical: "#FEF2F2", high: "#FFF7ED", medium: "#FFFBEB", low: "#F7FEE7" }[inc.severity] || "#F9FAFB";
                const statusColor = { open: "#D97706", investigating: "#2563EB", resolved: "#059669", closed: "#6B7280", escalated: "#DC2626" }[inc.status] || "#6B7280";
                return (
                  <motion.div key={inc._id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                    style={{ background: "#fff", borderRadius: 20, border: "1.5px solid rgba(12,90,62,0.08)", boxShadow: "0 2px 16px rgba(0,0,0,0.04)", marginBottom: 14, overflow: "hidden" }}>
                    <div style={{ padding: "16px 18px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 15, fontWeight: 800, color: "#0A1F14", marginBottom: 4 }}>{inc.title || inc.category?.replace(/_/g, " ")}</div>
                          <div style={{ fontSize: 11, color: "#94A3B8" }}>
                            {inc.orderId ? `Order #${String(inc.orderId?._id || inc.orderId).slice(-5)}` : ""} · {new Date(inc.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                          <span style={{ fontSize: 10, fontWeight: 800, color: sevColor, background: sevBg, padding: "3px 10px", borderRadius: 100, textTransform: "uppercase" }}>{inc.severity}</span>
                          <span style={{ fontSize: 10, fontWeight: 800, color: statusColor, background: "#F8FAFC", border: "1px solid #E2E8F0", padding: "3px 10px", borderRadius: 100, textTransform: "uppercase" }}>{inc.status}</span>
                        </div>
                      </div>
                      {inc.description && <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.5, marginBottom: 8 }}>{inc.description}</div>}
                      {inc.resolution?.action && (
                        <div style={{ background: "#F0FDF4", borderRadius: 12, padding: "10px 14px", border: "1px solid #D1FAE5" }}>
                          <div style={{ fontSize: 10, fontWeight: 800, color: "#065F46", textTransform: "uppercase", marginBottom: 4 }}>Resolution</div>
                          <div style={{ fontSize: 12, color: "#065F46", fontWeight: 600 }}>{inc.resolution.action}</div>
                          {inc.resolution.refundAmount > 0 && <div style={{ fontSize: 11, color: "#047857", marginTop: 4 }}>Refund: ₹{inc.resolution.refundAmount}</div>}
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )
        ) : displayGrouped.length === 0 ? (
          /* Empty state */
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            style={{ textAlign: "center", padding: "70px 20px" }}>
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              style={{ fontSize: 72, marginBottom: 16 }}
            >📦</motion.div>
            <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 20, fontWeight: 900, color: "#0B1F16", marginBottom: 8 }}>
              {activeTab === "active" ? "No active orders" : activeTab === "past" ? "No past orders" : "No orders yet"}
            </div>
            <div style={{ fontSize: 14, color: "#94A3B8", marginBottom: 28, lineHeight: 1.6 }}>
              {activeTab === "all" ? "Order medicines from nearby pharmacies" : "Check other tabs"}
            </div>
            <motion.button
              whileTap={{ scale: 0.96 }}
              whileHover={{ scale: 1.02 }}
              onClick={() => navigate("/pharmacies-near-you")}
              style={{
                height: 52, padding: "0 32px", borderRadius: 100, border: "none",
                background: `linear-gradient(135deg,${DEEP},${MID})`,
                color: "#fff", fontSize: 15, fontWeight: 800,
                fontFamily: "'Sora',sans-serif", cursor: "pointer",
                boxShadow: "0 8px 24px rgba(12,90,62,0.35)",
                display: "inline-flex", alignItems: "center", gap: 8,
              }}
            >
              <Zap style={{ width: 16, height: 16 }} /> Order Medicines
            </motion.button>
          </motion.div>
        ) : (
          /* Orders list — IDENTICAL render logic */
          <div style={{ paddingBottom: 8 }}>
            {displayGrouped.map((order) => {
              if (order.splits && order.splits.length > 0) {
                return (
                  <React.Fragment key={`parent-${order._id}`}>
                    {renderOrderCard(order, "Parent Order (Split)", `parent-${order._id}`)}
                    {order.splits
                      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                      .map((split) =>
                        renderOrderCard(
                          split,
                          `Split Order (Part of: #${String(order._id).slice(-6).toUpperCase()})`,
                          `split-${split._id}-parent-${order._id}`
                        )
                      )}
                  </React.Fragment>
                );
              } else {
                return renderOrderCard(
                  order,
                  order.parentOrder ? `Split Order (Part of: #${String(order.parentOrder).slice(-6).toUpperCase()})` : null,
                  order.parentOrder ? `split-${order._id}-parent-${order.parentOrder}` : `single-${order._id}`
                );
              }
            })}
          </div>
        )}
      </div>

      {/* ═══ REJECT DIALOG — IDENTICAL ═══ */}
      <Dialog open={rejectDialogOpen}
        onOpenChange={(open) => { if (!open) { setRejectDialogOpen(false); setRejectReason(""); } }}>
        <DialogContent className="force-light" style={{ borderRadius: 24 }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900 }}>Reject Order</DialogTitle>
          </DialogHeader>
          <div style={{ fontSize: 14, color: "#64748B", marginBottom: 10 }}>
            Please provide a reason for rejecting this order:
          </div>
          <textarea
            style={{
              width: "100%", minHeight: 80, borderRadius: 14,
              border: "1.5px solid #FCA5A5", padding: "12px 14px",
              fontSize: 15, outline: "none",
              fontFamily: "'Plus Jakarta Sans',sans-serif",
              boxSizing: "border-box", resize: "vertical", color: "#0B1F16",
            }}
            value={rejectReason} onChange={e => setRejectReason(e.target.value)}
            placeholder="Enter reason (required)" disabled={rejectSubmitting}
          />
          <DialogFooter style={{ gap: 8, marginTop: 4 }}>
            <Button variant="ghost" onClick={() => setRejectDialogOpen(false)} disabled={rejectSubmitting}>Cancel</Button>
            <Button
              disabled={!rejectReason.trim() || rejectSubmitting}
              style={{ background: "#EF4444", borderRadius: 12, fontFamily: "'Sora',sans-serif", fontWeight: 700 }}
              onClick={async () => {
                setRejectSubmitting(true);
                await handleUserConfirmRespond(pendingRejectOrderId, "rejected", rejectReason.trim());
                setRejectSubmitting(false);
              }}
            >
              Reject Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ PHARMACY REJECTION POPUP — IDENTICAL ═══ */}
      <Dialog open={showPharmacyRejectionPopup} onOpenChange={handleClosePharmacyRejectionPopup}>
        <DialogContent className="max-w-sm force-light" style={{ borderRadius: 24 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <DialogTitle style={{ fontFamily: "'Sora',sans-serif", fontSize: 16, fontWeight: 900 }}>
              Pharmacy Rejected Prescription
            </DialogTitle>
            <Button variant="ghost" size="icon" onClick={handleClosePharmacyRejectionPopup}><X className="h-5 w-5" /></Button>
          </div>
          <div style={{ fontSize: 14, color: "#64748B", marginBottom: 20, lineHeight: 1.6 }}>
            The pharmacy couldn't fulfill your prescription. What would you like to do?
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <motion.button whileTap={{ scale: 0.97 }}
              onClick={() => {
                if (rejectedPrescriptionOrder?._id) addHiddenRejectionId(rejectedPrescriptionOrder._id);
                setShowPharmacyRejectionPopup(false); setReuploadMode("manual"); setReuploadOrderData(rejectedPrescriptionOrder); setReuploadModalOpen(true);
              }}
              style={{ height: 50, borderRadius: 14, border: "none", background: `linear-gradient(135deg,${DEEP},${MID})`, color: "#fff", fontFamily: "'Sora',sans-serif", fontSize: 14, fontWeight: 800, cursor: "pointer", boxShadow: "0 6px 20px rgba(12,90,62,0.32)" }}>
              Choose Another Pharmacy
            </motion.button>
            <motion.button whileTap={{ scale: 0.97 }}
              onClick={() => {
                if (rejectedPrescriptionOrder?._id) addHiddenRejectionId(rejectedPrescriptionOrder._id);
                setShowPharmacyRejectionPopup(false); setReuploadMode("auto"); setReuploadOrderData(rejectedPrescriptionOrder); setReuploadModalOpen(true);
              }}
              style={{ height: 50, borderRadius: 14, border: `1.5px solid ${DEEP}30`, background: "#F0FAF5", color: DEEP, fontFamily: "'Sora',sans-serif", fontSize: 14, fontWeight: 800, cursor: "pointer" }}>
              Let GoDavaii Handle It
            </motion.button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══ MODALS — IDENTICAL PROPS ═══ */}
      <PrescriptionUploadModal
        open={reuploadModalOpen} onClose={() => setReuploadModalOpen(false)}
        userCity={reuploadOrderData?.address?.city || ""} userArea={reuploadOrderData?.address?.area || ""}
        afterOrder={() => setReuploadModalOpen(false)} initialMode={reuploadMode}
        initialNotes={reuploadOrderData?.notes || ""} initialFileUrl={reuploadOrderData?.prescriptionUrl || ""}
        initialAddress={reuploadOrderData?.address || {}}
      />
      <QuoteReviewModal
        open={quoteModalOpen} order={selectedOrder}
        onClose={() => setQuoteModalOpen(false)} onAccept={() => handleAcceptAndPay(selectedOrder)}
      />

      {/* ═══ SNACKBAR — IDENTICAL 3 colors ═══ */}
      <AnimatePresence>
        {snackbar.open && (
          <motion.div
            initial={{ y: -20, opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: -20, opacity: 0, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 380, damping: 26 }}
            onAnimationComplete={() => setTimeout(() => setSnackbar(s => ({ ...s, open: false })), 2200)}
            style={{
              position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)",
              zIndex: 9999, borderRadius: 100, padding: "12px 24px",
              color: "#fff", fontSize: 13.5, fontWeight: 700,
              boxShadow: "0 10px 30px rgba(0,0,0,0.20)", whiteSpace: "nowrap",
              fontFamily: "'Sora',sans-serif",
              background:
                snackbar.severity === "error" ? "#EF4444" :
                snackbar.severity === "info"  ? "#065F46" : "#059669",
            }}
          >
            {snackbar.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Animations */}
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes liveDot { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(0.7)} }
        @keyframes barShimmer { 0%{background-position:0% 0} 100%{background-position:200% 0} }
      `}</style>
    </div>
  );
}
